import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Icon from '../Icon';
import budgetService from '../../services/budgetService';
import {
  calculateUsageRate,
  calculateOverallUsageRate,
  getUsageColorClass,
  navigateMonth,
} from '../../utils/budgetCalculation';

/**
 * 예산 관리 카드
 * 예산 대비 실제 지출 비교, 예산 사용률을 표시한다.
 *
 * @param {Object} props
 * @param {string} props.currentMonth - 현재 선택된 월 ('YYYY-MM')
 */
const BudgetManagementCard = ({ currentMonth }) => {
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [comparisons, setComparisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchComparison = useCallback(async (month) => {
    setLoading(true);
    setError(null);
    try {
      const data = await budgetService.getComparison(month);
      setComparisons(data || []);
    } catch (err) {
      // 예산 기능 미설정, 테이블 없음, API 에러 모두 빈 상태로 처리
      setComparisons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComparison(selectedMonth);
  }, [selectedMonth, fetchComparison]);

  const handleMonth = (direction) => {
    setSelectedMonth(navigateMonth(selectedMonth, direction));
  };

  const formatMonthDisplay = (monthStr) => {
    const [year, month] = monthStr.split('-');
    return `${year}년 ${parseInt(month)}월`;
  };

  const hasBudget = comparisons.length > 0;
  const enrichedComparisons = comparisons.map((item) => ({
    ...item,
    usageRate: calculateUsageRate(item.actual, item.budget),
  }));
  const overallUsage = calculateOverallUsageRate(comparisons);
  const overallColorClass = getUsageColorClass(overallUsage);

  return (
    <div className="budget-card">
      <div className="budget-card__header">
        <Icon name="account_balance_wallet" size={20} />
        <h3 className="budget-card__title">예산 관리</h3>
        <div className="budget-card__nav">
          <button
            onClick={() => handleMonth('prev')}
            aria-label="이전 월"
            className="budget-card__nav-btn"
          >
            <Icon name="chevron_left" size={20} />
          </button>
          <span className="budget-card__nav-month">{formatMonthDisplay(selectedMonth)}</span>
          <button
            onClick={() => handleMonth('next')}
            aria-label="다음 월"
            className="budget-card__nav-btn"
          >
            <Icon name="chevron_right" size={20} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="budget-card__loading">
          <p>로딩 중...</p>
        </div>
      )}

      {error && (
        <div className="budget-card__error">
          <Icon name="error" size={24} color="#C4554D" />
          <p>{error}</p>
          <button
            onClick={() => fetchComparison(selectedMonth)}
            className="btn btn-primary"
          >
            재시도
          </button>
        </div>
      )}

      {!loading && !error && !hasBudget && (
        <div className="budget-card__empty">
          <Icon name="add_card" size={48} color="#9CA3AF" />
          <p className="budget-card__empty-text">예산 설정이 필요합니다</p>
          <p className="budget-card__empty-desc">모바일 앱에서 예산을 설정해주세요</p>
        </div>
      )}

      {!loading && !error && hasBudget && (
        <div className="budget-card__content">
          <div className="budget-card__overall">
            <span>전체 사용률</span>
            <span className={overallColorClass}>{overallUsage.toFixed(1)}%</span>
          </div>
          {enrichedComparisons.map((item) => (
            <div className="budget-card__item" key={item.category}>
              <span className="budget-card__item-category">{item.category}</span>
              <div className="budget-card__bar">
                <div
                  className={`budget-card__bar-fill ${getUsageColorClass(item.usageRate)}`}
                  style={{ width: `${Math.min(item.usageRate, 100)}%` }}
                />
              </div>
              <span className={`budget-card__item-rate ${getUsageColorClass(item.usageRate)}`}>
                {item.usageRate.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

BudgetManagementCard.propTypes = {
  currentMonth: PropTypes.string.isRequired,
};

export default BudgetManagementCard;
